import { useMemo } from "react";
import clsx from "clsx";
import { translate } from "../../translations";
import {
  ObjectId,
  OpinionOption,
  PopulatePartyOpinion,
  SKIP_VALUE,
  Statement,
  Stored,
  StudentOpinion,
} from "../../types";
import { getImageFromS3 } from "../../utils";

export default function StatementResult({
  statement,
  partyAnswers,
  answers,
  toggleHiddenComment,
  argumentsEnabled = false,
}: {
  statement: Stored<Statement>;
  partyAnswers: Array<PopulatePartyOpinion>;
  answers: Array<Stored<StudentOpinion>>;
  toggleHiddenComment: (id: ObjectId, hidden: boolean) => void;
  argumentsEnabled?: boolean;
}) {
  const options = useMemo(
    () => statement.opinionOptions.sort((o1, o2) => o2.order - o1.order),
    [statement],
  );
  const filteredAnswers = useMemo(
    () =>
      answers.filter(
        (a) => a.statementId === statement._id && a.opinion !== SKIP_VALUE,
      ),
    [answers, statement],
  );

  return (
    <>
      <div className="voting-session__results">
        <div className="voting-session__options">
          {options.map((option) => (
            <OptionResult
              option={option}
              answers={filteredAnswers}
              key={option.opinion}
            />
          ))}
        </div>
        {argumentsEnabled && (
          <div id="studentOpinions">
            <h2 className="voting-session__results-title">
              {translate("teacher.votingSession.statementPage.studentAnswers")}
            </h2>
            {filteredAnswers.filter(
              (a) => typeof a.comment === "string" && a.comment.length > 0,
            ).length > 0 ? (
              <div className="voting-session__options">
                {options.map((option) => (
                  <OptionAnswers
                    option={option}
                    answers={filteredAnswers}
                    key={option.opinion}
                    toggleHiddenComment={toggleHiddenComment}
                  />
                ))}
              </div>
            ) : (
              <p className="voting-session__results-empty">
                {translate(
                  "teacher.votingSession.statementPage.studentAnswersEmpty",
                )}
              </p>
            )}
          </div>
        )}
        <div id="partyOpinions">
          <h2 className="voting-session__results-title">
            {translate("teacher.votingSession.statementPage.partyAnswers")}
          </h2>
          <div className="voting-session__options">
            {options.map((option) => (
              <PartyAnswers
                option={option}
                partyAnswers={partyAnswers}
                key={option.opinion}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function OptionResult({
  answers,
  option,
}: {
  answers: Array<StudentOpinion>;
  option: OpinionOption;
}) {
  const percentage = useMemo(() => {
    const total = answers.length;
    if (total === 0) return 0;

    const count = answers.filter((a) => a.opinion === option.opinion).length;
    return Math.round((count / total) * 100);
  }, [option, answers]);

  return (
    <div
      className="session-option voting-session__option"
      style={{ "--option-color": option.color }}
    >
      <p className="session-option__text">{percentage}%</p>
      <div className="session-option__score-wrapper">
        <div
          className="session-option__score"
          style={{ "--score": percentage }}
        ></div>
      </div>

      <p className="session-option__text">{option.opinion}</p>
    </div>
  );
}

function OptionAnswers({
  answers,
  option,
  toggleHiddenComment,
}: {
  answers: Array<Stored<StudentOpinion>>;
  option: OpinionOption;
  toggleHiddenComment: (id: ObjectId, hidden: boolean) => void;
}) {
  const filtered = answers.filter(
    (a) =>
      a.opinion === option.opinion &&
      typeof a.comment === "string" &&
      a.comment.length > 0,
  );

  return (
    <div className="session-arguments voting-session__option">
      <div
        className="session-arguments__title"
        style={{ "--option-color": option.color }}
      >
        {option.opinion}
      </div>
      {filtered.map((a) => (
        <span
          className={clsx(
            "session-arguments__item session-arguments__item--student",
            {
              "session-arguments__item--hidden": a.hidden,
            },
          )}
          key={a._id}
        >
          <span className="session-arguments__item-text">{a.comment}</span>
          <button
            className="session-arguments__toggle"
            onClick={() => toggleHiddenComment(a._id, !a.hidden)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="currentcolor"
            >
              {a.hidden ? (
                <path d="M12.6559 12.2393C13.3851 11.5101 13.7497 10.6247 13.7497 9.58301C13.7497 8.54134 13.3851 7.65592 12.6559 6.92676C11.9268 6.19759 11.0413 5.83301 9.99967 5.83301C8.95801 5.83301 8.07259 6.19759 7.34342 6.92676C6.61426 7.65592 6.24967 8.54134 6.24967 9.58301C6.24967 10.6247 6.61426 11.5101 7.34342 12.2393C8.07259 12.9684 8.95801 13.333 9.99967 13.333C11.0413 13.333 11.9268 12.9684 12.6559 12.2393ZM8.40593 11.1768C7.96843 10.7393 7.74967 10.208 7.74967 9.58301C7.74967 8.95801 7.96843 8.42676 8.40593 7.98926C8.84343 7.55176 9.37467 7.33301 9.99967 7.33301C10.6247 7.33301 11.1559 7.55176 11.5934 7.98926C12.0309 8.42676 12.2497 8.95801 12.2497 9.58301C12.2497 10.208 12.0309 10.7393 11.5934 11.1768C11.1559 11.6143 10.6247 11.833 9.99967 11.833C9.37467 11.833 8.84343 11.6143 8.40593 11.1768ZM4.45801 14.1351C2.79134 13.0031 1.58301 11.4858 0.833008 9.58301C1.58301 7.68023 2.79134 6.16287 4.45801 5.03092C6.12467 3.89898 7.9719 3.33301 9.99967 3.33301C12.0275 3.33301 13.8747 3.89898 15.5413 5.03092C17.208 6.16287 18.4163 7.68023 19.1663 9.58301C18.4163 11.4858 17.208 13.0031 15.5413 14.1351C13.8747 15.267 12.0275 15.833 9.99967 15.833C7.9719 15.833 6.12467 15.267 4.45801 14.1351ZM14.3226 12.9268C15.6351 12.1004 16.6386 10.9858 17.333 9.58301C16.6386 8.18023 15.6351 7.06565 14.3226 6.23926C13.0101 5.41287 11.5691 4.99967 9.99967 4.99967C8.43023 4.99967 6.98926 5.41287 5.67676 6.23926C4.36426 7.06565 3.36079 8.18023 2.66634 9.58301C3.36079 10.9858 4.36426 12.1004 5.67676 12.9268C6.98926 13.7531 8.43023 14.1663 9.99967 14.1663C11.5691 14.1663 13.0101 13.7531 14.3226 12.9268Z" />
              ) : (
                <path d="M13.4163 11.083L12.208 9.87468C12.333 9.2219 12.1455 8.61079 11.6455 8.04134C11.1455 7.4719 10.4997 7.24967 9.70801 7.37467L8.49967 6.16634C8.73579 6.05523 8.97537 5.9719 9.21842 5.91634C9.46148 5.86079 9.7219 5.83301 9.99967 5.83301C11.0413 5.83301 11.9268 6.19759 12.6559 6.92676C13.3851 7.65592 13.7497 8.54134 13.7497 9.58301C13.7497 9.86079 13.7219 10.1212 13.6663 10.3643C13.6108 10.6073 13.5275 10.8469 13.4163 11.083ZM16.083 13.708L14.8747 12.5413C15.4025 12.1386 15.8712 11.6976 16.2809 11.2184C16.6906 10.7393 17.0413 10.1941 17.333 9.58301C16.6386 8.18023 15.642 7.06565 14.3434 6.23926C13.0448 5.41287 11.5969 4.99967 9.99967 4.99967C9.5969 4.99967 9.20106 5.02745 8.81217 5.08301C8.42329 5.13856 8.04134 5.2219 7.66634 5.33301L6.37467 4.04134C6.94412 3.80523 7.52745 3.62815 8.12467 3.51009C8.7219 3.39204 9.3469 3.33301 9.99967 3.33301C12.0969 3.33301 13.965 3.91287 15.6038 5.07259C17.2427 6.23231 18.4302 7.73579 19.1663 9.58301C18.8469 10.4025 18.4268 11.1629 17.9059 11.8643C17.3851 12.5656 16.7775 13.1802 16.083 13.708ZM16.4997 18.833L12.9997 15.3747C12.5136 15.5275 12.024 15.642 11.5309 15.7184C11.0379 15.7948 10.5275 15.833 9.99967 15.833C7.90245 15.833 6.0344 15.2531 4.39551 14.0934C2.75662 12.9337 1.56912 11.4302 0.833008 9.58301C1.12467 8.8469 1.49273 8.16287 1.93717 7.53092C2.38162 6.89898 2.88856 6.33301 3.45801 5.83301L1.16634 3.49967L2.33301 2.33301L17.6663 17.6663L16.4997 18.833ZM4.62467 6.99967C4.2219 7.36079 3.85384 7.75662 3.52051 8.18717C3.18717 8.61773 2.90245 9.08301 2.66634 9.58301C3.36079 10.9858 4.35731 12.1004 5.65592 12.9268C6.95454 13.7531 8.40245 14.1663 9.99967 14.1663C10.2775 14.1663 10.5483 14.149 10.8122 14.1143C11.0761 14.0795 11.3469 14.0413 11.6247 13.9997L10.8747 13.208C10.7219 13.2497 10.5761 13.2809 10.4372 13.3018C10.2983 13.3226 10.1525 13.333 9.99967 13.333C8.95801 13.333 8.07259 12.9684 7.34342 12.2393C6.61426 11.5101 6.24967 10.6247 6.24967 9.58301C6.24967 9.43023 6.26009 9.2844 6.28092 9.14551C6.30176 9.00662 6.33301 8.86079 6.37467 8.70801L4.62467 6.99967Z" />
              )}
            </svg>
            {translate([
              "teacher",
              "votingSession",
              "toggleComment",
              a.hidden ? "show" : "hide",
            ])}
          </button>
        </span>
      ))}
    </div>
  );
}

export function PartyAnswers({
  option,
  partyAnswers,
  itemClass,
}: {
  option: OpinionOption;
  partyAnswers: Array<PopulatePartyOpinion>;
  itemClass?: string;
}) {
  const filtered = partyAnswers.filter((p) => p.opinion === option.opinion);

  return (
    <div className="session-arguments voting-session__option">
      <div
        className="session-arguments__title"
        style={{ "--option-color": option.color }}
      >
        {option.opinion}
      </div>
      {filtered.map((a) => (
        <span
          className={clsx("session-arguments__item party-answer", itemClass)}
          key={a._id}
        >
          {/* We don't need alt text, since the party logo is right below */}
          {a.partyId.hasLogo && (
            <img
              src={getImageFromS3(`parties/${a.partyId._id}`)}
              alt=""
              className="party-answer__logo"
            />
          )}
          <details className="party-answer__details">
            <summary className="party-answer__summary">
              {a.partyId.name}
            </summary>
            {a.comment}
          </details>
        </span>
      ))}
    </div>
  );
}
